/**
 * The stateful demo backend.
 *
 * Demo mode is not a fixed dataset any more. This module owns a live in-memory
 * database, seeded from mock/data.ts, that actually mutates: answer an
 * availability request and the event moves, assign a coach and the counter goes
 * up, approve a qualification and that coach starts seeing those events.
 *
 * Three rules it holds itself to:
 *
 *  1. It mirrors Postgres, it does not improve on it. Every write here has a
 *     matching trigger or policy in supabase/migrations. Assigning a coach
 *     enqueues the same three notifications enqueue_assignment_notifications()
 *     would, and recomputes the same earning. Where the database would refuse a
 *     write, this refuses it with the same reason.
 *  2. Nothing here leaks upwards. src/lib/api.ts calls into this module only
 *     when Supabase is not configured; the function signatures and return
 *     shapes on the other side are identical either way, so no screen ever
 *     knows which backend it is talking to.
 *  3. State survives a refresh. Everything is written to localStorage after
 *     every mutation, so reloading mid-demo does not lose the story. A stale
 *     snapshot (different shape, or older than the freshness window) is
 *     discarded rather than shown, because the seeded dates are relative to
 *     the clock and would otherwise drift out of the story.
 */

import {
  createSeedData,
  demoDb,
  type DemoTables,
} from './data'
import { calculateEventPay } from './pay'
import {
  buildAssignmentNotifications,
  buildAvailabilityRequestNotification,
  buildQualificationApprovedNotification,
  buildQualificationDecisionNotification,
  type NewNotification,
} from './notify'
import type {
  Assignment,
  Availability,
  AvailabilityResponse,
  ChecklistItem,
  ChecklistItemState,
  EventEligibleCoach,
  EventRow,
  MaterialsCheck,
  PostEventReport,
  PushSubscriptionRow,
  Qualification,
  Service,
} from '../../types/db'

/* -------------------------------------------------------------------------- */
/* Persistence                                                                */
/* -------------------------------------------------------------------------- */

const STORAGE_KEY = 'lafolie-demo-store'

/** Bump when DemoTables changes shape, so old snapshots are dropped. */
const STORAGE_VERSION = 1

/**
 * Seeded dates are relative to the clock at seed time, so a snapshot taken
 * yesterday would show "tomorrow's party" in the past. Twelve hours comfortably
 * covers a demo session and a lunch break.
 */
const MAX_SNAPSHOT_AGE_MS = 12 * 3_600_000

interface PersistedState {
  v: number
  seededAt: string
  tables: DemoTables
}

const TABLE_KEYS = Object.keys(demoDb) as Array<keyof DemoTables>

let hydrated = false
let seededAt = new Date().toISOString()

function storage(): Storage | null {
  try {
    // localStorage, not sessionStorage: the demo session key already lives
    // there, and an installed PWA is killed and relaunched between taps on a
    // phone. The 12 hour age cap above is what keeps stale snapshots out.
    if (typeof localStorage === 'undefined') return null
    return localStorage
  } catch {
    // Private mode and "block site data" both throw on access, not on use.
    return null
  }
}

function isUsableSnapshot(parsed: unknown): parsed is PersistedState {
  if (typeof parsed !== 'object' || parsed === null) return false
  const candidate = parsed as Partial<PersistedState>
  if (candidate.v !== STORAGE_VERSION) return false
  if (typeof candidate.seededAt !== 'string') return false
  if (Date.now() - new Date(candidate.seededAt).getTime() > MAX_SNAPSHOT_AGE_MS) return false
  const tables = candidate.tables as Record<string, unknown> | undefined
  if (!tables) return false
  return TABLE_KEYS.every((key) => Array.isArray(tables[key]))
}

function hydrate(): void {
  if (hydrated) return
  hydrated = true
  const store = storage()
  if (!store) return
  try {
    const raw = store.getItem(STORAGE_KEY)
    if (!raw) return
    const parsed: unknown = JSON.parse(raw)
    if (!isUsableSnapshot(parsed)) {
      discard(store)
      return
    }
    Object.assign(demoDb, parsed.tables)
    seededAt = parsed.seededAt
  } catch {
    // A corrupt snapshot is not worth a broken demo.
    discard(store)
  }
}

/** Drop an unusable snapshot and make sure the tables are a known good seed. */
function discard(store: Storage): void {
  try {
    store.removeItem(STORAGE_KEY)
  } catch {
    /* nothing else to do */
  }
  Object.assign(demoDb, createSeedData())
  seededAt = new Date().toISOString()
}

function persist(): void {
  const store = storage()
  if (!store) return
  try {
    const state: PersistedState = { v: STORAGE_VERSION, seededAt, tables: demoDb }
    store.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Quota or a blocked store. The demo keeps working in memory.
  }
}

/* -------------------------------------------------------------------------- */
/* Change notification                                                        */
/* -------------------------------------------------------------------------- */

type Listener = () => void
const listeners = new Set<Listener>()

/** Called after every mutation and after a reset. Returns an unsubscribe. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function notify(): void {
  for (const listener of [...listeners]) {
    try {
      listener()
    } catch {
      // A broken listener must not take a write down with it.
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Access                                                                     */
/* -------------------------------------------------------------------------- */

/** The live tables. Internal: reads from api.ts go through snapshot(). */
function tables(): DemoTables {
  hydrate()
  return demoDb
}

/**
 * The dispatcher, on demand.
 *
 * On the real backend pg_cron ticks every five minutes, hands the due rows to
 * the dispatch-notifications Edge Function, and that marks them sent. There is
 * no cron in demo mode, so without this a reminder queued by an assignment
 * would sit at 'pending' forever and never reach the coach's inbox. Running it
 * on read is the closest honest equivalent: the queue drains by itself, just
 * faster than it would in production.
 *
 * Only rows that are actually due move, exactly like the SQL.
 */
function dispatchDue(db: DemoTables): boolean {
  const now = Date.now()
  let changed = false
  for (const row of db.notificationQueue) {
    if (row.status !== 'pending') continue
    if (new Date(row.send_at).getTime() > now) continue
    row.status = 'sent'
    row.sent_at = new Date().toISOString()
    row.attempts += 1
    changed = true
  }
  return changed
}

/**
 * A deep copy of the whole demo database.
 *
 * Reads hand out clones so a screen can never mutate the store by holding on to
 * a row, and so every read returns fresh object identities for React. Every
 * value in DemoTables is JSON-safe by construction.
 */
export function snapshot(): DemoTables {
  const db = tables()
  // Deliberately not broadcast: whoever asked is about to receive this data.
  if (dispatchDue(db)) persist()
  return JSON.parse(JSON.stringify(db)) as DemoTables
}

/** Runs a write against the live tables, then persists and notifies. */
function mutate<T>(fn: (db: DemoTables) => T): T {
  const db = tables()
  const result = fn(db)
  // An assignment queues its confirmation with send_at = now, so drain
  // immediately: the coach should see it the moment Michel taps assign.
  dispatchDue(db)
  persist()
  notify()
  return result
}

/** Throws the seed away and rebuilds it from the current clock. */
export function reset(): void {
  Object.assign(demoDb, createSeedData())
  seededAt = new Date().toISOString()
  hydrated = true
  persist()
  notify()
}

function nowIso(): string {
  return new Date().toISOString()
}

function newId(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  // Good enough for a demo row id when randomUUID is unavailable.
  return 'demo-' + Math.random().toString(16).slice(2) + Date.now().toString(16)
}

function fail(message: string): never {
  throw new Error(message)
}

/* -------------------------------------------------------------------------- */
/* Lookups                                                                    */
/* -------------------------------------------------------------------------- */

function eventOf(db: DemoTables, eventId: string): EventRow {
  return db.events.find((e) => e.id === eventId) ?? fail('That event no longer exists.')
}

function serviceOf(db: DemoTables, event: EventRow): Service | undefined {
  return db.services.find((s) => s.id === event.service_id)
}

function activeAssignment(
  db: DemoTables,
  eventId: string,
  coachId: string,
): Assignment | undefined {
  return db.assignments.find(
    (a) => a.event_id === eventId && a.coach_id === coachId && a.status !== 'cancelled',
  )
}

/* -------------------------------------------------------------------------- */
/* The qualification gate, mirrored from migration 02                         */
/* -------------------------------------------------------------------------- */

/** Mirror of public.is_coach_qualified(). Approved, and never revoked. */
export function isCoachQualified(db: DemoTables, coachId: string, serviceId: string): boolean {
  return db.qualifications.some(
    (q) =>
      q.coach_id === coachId &&
      q.service_id === serviceId &&
      q.status === 'approved' &&
      q.revoked_at === null,
  )
}

/**
 * Mirror of public.coach_can_see_event(): assigned to it, or asked about it, or
 * qualified for its service while it is still live. This is the rule the whole
 * product rests on, and RLS enforces the same one server side.
 */
export function coachCanSeeEvent(
  db: DemoTables,
  eventId: string,
  coachId: string,
  now: Date = new Date(),
): boolean {
  if (db.assignments.some((a) => a.event_id === eventId && a.coach_id === coachId)) return true
  if (db.availabilities.some((a) => a.event_id === eventId && a.coach_id === coachId)) return true
  const event = db.events.find((e) => e.id === eventId)
  if (!event) return false
  return isEventOpenToQualifiedCoach(event, now) && isCoachQualified(db, coachId, event.service_id)
}

/** The "still live" half of coach_can_see_event(), reused by the event lists. */
export function isEventOpenToQualifiedCoach(event: EventRow, now: Date = new Date()): boolean {
  return (
    event.cancelled_at === null &&
    (event.status === 'scheduled' || event.status === 'staffed') &&
    // Stays visible for a day after it starts so same-day lookups still work.
    new Date(event.starts_at).getTime() > now.getTime() - 24 * 3_600_000
  )
}

/**
 * Mirror of the public.event_eligible_coaches view: every (event, qualified
 * coach) pair with that coach's answer and whether they are already on it.
 * This is the single place the demo evaluates the gate for staffing.
 */
export function eventEligibleCoaches(db: DemoTables, eventId: string): EventEligibleCoach[] {
  const event = db.events.find((e) => e.id === eventId)
  if (!event || event.cancelled_at !== null) return []
  const rows: EventEligibleCoach[] = []
  for (const q of db.qualifications) {
    if (q.service_id !== event.service_id) continue
    if (q.status !== 'approved' || q.revoked_at !== null) continue
    const coach = db.profiles.find((p) => p.id === q.coach_id)
    if (!coach || coach.role !== 'coach' || coach.status !== 'active') continue
    const availability = db.availabilities.find(
      (a) => a.event_id === event.id && a.coach_id === coach.id,
    )
    const assignment = activeAssignment(db, event.id, coach.id)
    rows.push({
      event_id: event.id,
      service_id: event.service_id,
      starts_at: event.starts_at,
      coach_id: coach.id,
      full_name: coach.full_name,
      phone: coach.phone,
      seniority: coach.seniority,
      qualification_id: q.id,
      approved_at: q.approved_at,
      availability_response: availability?.response ?? 'pending',
      responded_at: availability?.responded_at ?? null,
      is_assigned: assignment !== undefined,
      assignment_id: assignment?.id ?? null,
    })
  }
  return rows
}

/* -------------------------------------------------------------------------- */
/* Trigger mirrors                                                            */
/* -------------------------------------------------------------------------- */

function insertNotification(db: DemoTables, row: NewNotification, at: string): void {
  const existing = db.notificationQueue.find((n) => n.dedupe_key === row.dedupe_key)
  if (existing) return
  db.notificationQueue.push({ ...row, id: newId(), created_at: at })
}

/**
 * Mirror of public.enqueue_assignment_notifications(). Idempotent through
 * dedupe_key: re-running after a reschedule moves send_at on rows that have not
 * gone out yet and never touches one that has.
 *
 * One deliberate difference from the SQL as shipped: a row that was cancelled
 * when the coach was stood down comes back to 'pending' when the same coach is
 * put back on. That is what trg_assignments_after_update says it does
 * ("un-cancelled -> both come back"), but its "on conflict do update ... where
 * status = 'pending'" leaves the cancelled row blocking the insert, so a coach
 * re-assigned after a stand-down silently got no T-48h / T-24h reminders. The
 * row is revived in place because dedupe_key is unique and the cancelled row
 * never reached anyone. The matching SQL fix is a follow-up migration.
 */
function enqueueAssignmentNotifications(
  db: DemoTables,
  assignmentId: string,
  at: string = nowIso(),
): void {
  const assignment = db.assignments.find((a) => a.id === assignmentId)
  if (!assignment || assignment.status === 'cancelled') return
  const event = db.events.find((e) => e.id === assignment.event_id)
  if (!event || event.cancelled_at !== null || event.status === 'cancelled') return

  for (const row of buildAssignmentNotifications(assignment, event, serviceOf(db, event), at)) {
    const existing = db.notificationQueue.find((n) => n.dedupe_key === row.dedupe_key)
    if (!existing) {
      db.notificationQueue.push({ ...row, id: newId(), created_at: at })
      continue
    }
    if (existing.status === 'cancelled') {
      // Stood down and put back on: the cancelled row was never delivered, so
      // it is simply queued again with the current send_at and payload.
      existing.status = 'pending'
      existing.send_at = row.send_at
      existing.payload = row.payload
      existing.sent_at = null
      existing.last_error = null
      existing.acknowledged_at = null
      continue
    }
    // The confirmation is "on conflict do nothing"; the reminders move.
    if (row.type !== 'assignment_created' && existing.status === 'pending') {
      existing.send_at = row.send_at
      existing.payload = row.payload
    }
  }
}

/** Mirror of public.cancel_pending_notifications(). Sent rows stay: they are the inbox. */
function cancelPendingNotifications(
  db: DemoTables,
  filter: { assignmentId?: string; eventId?: string; coachId?: string },
): number {
  if (!filter.assignmentId && !filter.eventId) return 0
  let count = 0
  for (const row of db.notificationQueue) {
    if (row.status !== 'pending') continue
    if (filter.assignmentId && row.assignment_id !== filter.assignmentId) continue
    if (filter.eventId && row.event_id !== filter.eventId) continue
    if (filter.coachId && row.coach_id !== filter.coachId) continue
    row.status = 'cancelled'
    count += 1
  }
  return count
}

/**
 * Keeps events.status in step with the headcount: 'staffed' once the required
 * number of coaches is on it, back to 'scheduled' when somebody comes off.
 *
 * There is no trigger for this in the locked migrations, so it is an app-level
 * rule: api.ts does the same update on the live backend after assignCoach and
 * unassignCoach. Draft, completed and cancelled events are never touched.
 */
function syncEventStaffedStatus(db: DemoTables, eventId: string, at: string): void {
  const event = db.events.find((e) => e.id === eventId)
  if (!event || event.cancelled_at !== null) return
  if (event.status !== 'scheduled' && event.status !== 'staffed') return
  const active = db.assignments.filter(
    (a) => a.event_id === eventId && a.status !== 'cancelled',
  ).length
  const next = active >= event.coaches_required ? 'staffed' : 'scheduled'
  if (event.status !== next) {
    event.status = next
    event.updated_at = at
  }
}

/** Mirror of public.recompute_earning_for_assignment(). Idempotent. */
function recomputeEarningForAssignment(db: DemoTables, assignmentId: string): void {
  const assignment = db.assignments.find((a) => a.id === assignmentId)
  if (!assignment) return
  const event = db.events.find((e) => e.id === assignment.event_id)
  if (!event) return
  const coach = db.profiles.find((p) => p.id === assignment.coach_id)

  const isVoid =
    assignment.status === 'cancelled' ||
    assignment.status === 'no_show' ||
    event.status === 'cancelled' ||
    event.cancelled_at !== null

  const breakdown = calculateEventPay(db.payRates, {
    serviceId: event.service_id,
    seniority: coach?.seniority ?? 'standard',
    locationZone: event.location_zone,
    durationMinutes: event.duration_minutes,
    onDate: event.event_date,
  })
  const periodMonth =
    new Date(new Date(event.starts_at).getTime() + 4 * 3_600_000).toISOString().slice(0, 8) + '01'
  const amount = isVoid ? 0 : breakdown.amount_aed
  const status = isVoid ? 'void' : 'draft'
  const at = nowIso()

  const existing = db.earnings.find((e) => e.assignment_id === assignment.id)
  if (!existing) {
    db.earnings.push({
      id: newId(),
      assignment_id: assignment.id,
      coach_id: assignment.coach_id,
      event_id: assignment.event_id,
      pay_rate_id: breakdown.pay_rate_id,
      amount_aed: amount,
      currency: 'AED',
      breakdown,
      status,
      period_month: periodMonth,
      computed_at: at,
      approved_by: null,
      approved_at: null,
      paid_at: null,
      payout_reference: null,
      // The shadow-month gate. Never flipped by any write in this app.
      is_visible_to_coach: false,
      notes: null,
      created_at: at,
      updated_at: at,
    })
    return
  }
  // Money Pascal has already approved or paid is never silently rewritten.
  if (existing.status === 'approved' || existing.status === 'paid') return
  existing.pay_rate_id = breakdown.pay_rate_id
  existing.amount_aed = amount
  existing.breakdown = breakdown
  existing.status = status
  existing.period_month = periodMonth
  existing.computed_at = at
  existing.updated_at = at
}

/* -------------------------------------------------------------------------- */
/* Availability                                                               */
/* -------------------------------------------------------------------------- */

/**
 * A coach answers, or changes an answer. Mirrors availabilities_insert_own /
 * availabilities_update_own plus trg_availabilities_stamp: a coach may answer
 * any event they can see, including volunteering for one they were not asked
 * about.
 */
export function respondToAvailability(
  eventId: string,
  coachId: string,
  response: AvailabilityResponse,
  note: string | null,
): Availability {
  return mutate((db) => {
    if (!coachCanSeeEvent(db, eventId, coachId)) {
      fail('That event is not open to you.')
    }
    const at = nowIso()
    const existing = db.availabilities.find(
      (a) => a.event_id === eventId && a.coach_id === coachId,
    )
    if (existing) {
      const changed = existing.response !== response
      existing.response = response
      existing.note = note
      if (response !== 'pending' && changed) existing.responded_at = at
      existing.updated_at = at
      return { ...existing }
    }
    const row: Availability = {
      id: newId(),
      event_id: eventId,
      coach_id: coachId,
      response,
      responded_at: response === 'pending' ? null : at,
      note,
      requested_at: at,
      // Nobody asked: the coach volunteered off the open events list.
      requested_by: null,
      created_at: at,
      updated_at: at,
    }
    db.availabilities.push(row)
    return { ...row }
  })
}

/**
 * Michel asks the qualified coaches whether they can work an event. Creates the
 * pending rows and the in-app request, and never overwrites an answer somebody
 * has already given.
 */
export function requestAvailability(
  eventId: string,
  coachIds: string[],
  requestedBy: string,
): number {
  return mutate((db) => {
    const event = eventOf(db, eventId)
    const service = serviceOf(db, event)
    const at = nowIso()
    let created = 0
    for (const coachId of coachIds) {
      if (!isCoachQualified(db, coachId, event.service_id)) continue
      const existing = db.availabilities.find(
        (a) => a.event_id === eventId && a.coach_id === coachId,
      )
      if (!existing) {
        db.availabilities.push({
          id: newId(),
          event_id: eventId,
          coach_id: coachId,
          response: 'pending',
          responded_at: null,
          note: null,
          requested_at: at,
          requested_by: requestedBy,
          created_at: at,
          updated_at: at,
        })
        created += 1
      }
      insertNotification(
        db,
        buildAvailabilityRequestNotification(coachId, event, service, at),
        at,
      )
    }
    return created
  })
}

/* -------------------------------------------------------------------------- */
/* Assignment                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Puts a coach on an event. Fires the same two triggers the database would:
 * the reminder queue is filled and the draft earning is computed.
 *
 * The qualification gate is checked here as well as in api.ts, because in demo
 * mode there is no Postgres to refuse the row.
 *
 * Assigning a coach who is already on the event only sets is_lead, matching the
 * Supabase path: a second tap must not demote somebody who has confirmed, and
 * must not re-stamp assigned_at under reminders that have already gone out.
 */
export function assignCoach(
  eventId: string,
  coachId: string,
  assignedBy: string,
  isLead: boolean,
): Assignment {
  return mutate((db) => {
    const event = eventOf(db, eventId)
    if (!isCoachQualified(db, coachId, event.service_id)) {
      fail('That coach is not qualified for this service yet. Approve the qualification first.')
    }
    const at = nowIso()
    const existing = db.assignments.find(
      (a) => a.event_id === eventId && a.coach_id === coachId,
    )
    if (existing) {
      if (existing.status !== 'cancelled') {
        existing.is_lead = isLead
        existing.updated_at = at
        return { ...existing }
      }
      // Standing a cancelled coach back up is a real re-assignment: the
      // reminders were cancelled with it and have to be queued again.
      existing.status = 'assigned'
      existing.is_lead = isLead
      existing.assigned_at = at
      existing.assigned_by = assignedBy
      existing.confirmed_at = null
      existing.cancelled_at = null
      existing.cancellation_reason = null
      existing.updated_at = at
      enqueueAssignmentNotifications(db, existing.id, at)
      recomputeEarningForAssignment(db, existing.id)
      syncEventStaffedStatus(db, eventId, at)
      return { ...existing }
    }
    const row: Assignment = {
      id: newId(),
      event_id: eventId,
      coach_id: coachId,
      status: 'assigned',
      is_lead: isLead,
      assigned_at: at,
      assigned_by: assignedBy,
      confirmed_at: null,
      cancelled_at: null,
      cancellation_reason: null,
      created_at: at,
      updated_at: at,
    }
    db.assignments.push(row)
    enqueueAssignmentNotifications(db, row.id, at)
    recomputeEarningForAssignment(db, row.id)
    syncEventStaffedStatus(db, eventId, at)
    return { ...row }
  })
}

/** Stand a coach down. Mirrors the cancelled branch of trg_assignments_after_update. */
export function cancelAssignment(assignmentId: string, reason: string): Assignment {
  return mutate((db) => {
    const row =
      db.assignments.find((a) => a.id === assignmentId) ??
      fail('That assignment no longer exists.')
    if (row.status !== 'cancelled') {
      const at = nowIso()
      row.status = 'cancelled'
      row.cancelled_at = at
      row.cancellation_reason = reason
      row.updated_at = at
      cancelPendingNotifications(db, { assignmentId: row.id })
      recomputeEarningForAssignment(db, row.id)
      syncEventStaffedStatus(db, row.event_id, at)
    }
    return { ...row }
  })
}

/**
 * The coach taps "I will be there". A second tap changes nothing: the first
 * confirmed_at stands, so a double tap never re-stamps or duplicates anything.
 */
export function confirmAssignment(assignmentId: string): Assignment {
  return mutate((db) => {
    const row =
      db.assignments.find((a) => a.id === assignmentId) ??
      fail('That assignment no longer exists.')
    if (row.status === 'cancelled') {
      fail('You are no longer on this event, so there is nothing to confirm.')
    }
    if (row.status !== 'assigned') return { ...row }
    const at = nowIso()
    row.status = 'confirmed'
    row.confirmed_at = at
    row.updated_at = at
    return { ...row }
  })
}

/* -------------------------------------------------------------------------- */
/* Materials                                                                  */
/* -------------------------------------------------------------------------- */

export function setChecklistItemState(
  itemId: string,
  state: ChecklistItemState,
  note: string | null,
  updatedBy: string | null,
): ChecklistItem {
  return mutate((db) => {
    const item =
      db.checklistItems.find((i) => i.id === itemId) ?? fail('That checklist item is gone.')
    item.state = state
    item.note = note
    item.updated_by = updatedBy
    item.updated_at = nowIso()
    return { ...item }
  })
}

export function setAllChecklistItems(
  materialsCheckId: string,
  state: ChecklistItemState,
  updatedBy: string | null,
): number {
  return mutate((db) => {
    const at = nowIso()
    let count = 0
    for (const item of db.checklistItems) {
      if (item.materials_check_id !== materialsCheckId) continue
      item.state = state
      item.updated_by = updatedBy
      item.updated_at = at
      count += 1
    }
    return count
  })
}

/**
 * Sign off a check-out or a check-in.
 *
 * "All returned" is the happy path and closes the check. Anything else is an
 * issue with a note attached, which is what Gina and Michel actually chase.
 */
export function completeMaterialsCheck(
  materialsCheckId: string,
  checkedBy: string,
  allAccountedFor: boolean,
  note: string | null,
): MaterialsCheck {
  return mutate((db) => {
    const check =
      db.materialsChecks.find((m) => m.id === materialsCheckId) ??
      fail('That materials checklist is gone.')
    const at = nowIso()
    const items = db.checklistItems.filter((i) => i.materials_check_id === check.id)
    if (allAccountedFor) {
      for (const item of items) {
        if (item.state === 'pending') {
          item.state = 'ok'
          item.updated_by = checkedBy
          item.updated_at = at
        }
      }
    }
    const hasProblem = items.some((i) => i.state === 'missing' || i.state === 'damaged')
    check.status = allAccountedFor && !hasProblem ? 'complete' : 'issue'
    check.checked_by = checkedBy
    check.checked_at = at
    check.notes = note
    check.updated_at = at
    return { ...check }
  })
}

/* -------------------------------------------------------------------------- */
/* Post-event report                                                          */
/* -------------------------------------------------------------------------- */

export interface DemoReportInput {
  eventId: string
  coachId: string
  overallRating: number | null
  attendanceCount: number | null
  wentWell: string | null
  issues: string | null
  incident: boolean
  incidentDetails: string | null
  clientFeedback: string | null
  photoPaths: string[]
}

/**
 * Submit or correct the questionnaire. Mirrors post_event_reports_insert_own:
 * only a coach who is actually on the event may file one, which is exactly what
 * is_assigned_to_event() checks server side.
 */
export function submitPostEventReport(input: DemoReportInput): PostEventReport {
  return mutate((db) => {
    const assignment = activeAssignment(db, input.eventId, input.coachId)
    if (!assignment) {
      fail('Only a coach assigned to this event can file its report.')
    }
    const at = nowIso()
    const existing = db.postEventReports.find(
      (r) => r.event_id === input.eventId && r.coach_id === input.coachId,
    )
    const fields = {
      assignment_id: assignment.id,
      overall_rating: input.overallRating,
      attendance_count: input.attendanceCount,
      went_well: input.wentWell,
      issues: input.issues,
      incident: input.incident,
      incident_details: input.incidentDetails,
      client_feedback: input.clientFeedback,
      photo_paths: input.photoPaths,
    }
    if (existing) {
      Object.assign(existing, fields, { submitted_at: at, updated_at: at })
      return { ...existing }
    }
    const row: PostEventReport = {
      id: newId(),
      event_id: input.eventId,
      coach_id: input.coachId,
      submitted_at: at,
      created_at: at,
      updated_at: at,
      ...fields,
    }
    db.postEventReports.push(row)
    return { ...row }
  })
}

/* -------------------------------------------------------------------------- */
/* Training and qualification                                                 */
/* -------------------------------------------------------------------------- */

export function markModuleComplete(coachId: string, moduleId: string): void {
  mutate((db) => {
    const already = db.moduleCompletions.some(
      (c) => c.coach_id === coachId && c.module_id === moduleId,
    )
    if (already) return
    const at = nowIso()
    db.moduleCompletions.push({
      id: newId(),
      coach_id: coachId,
      module_id: moduleId,
      completed_at: at,
      score: null,
      source: 'in_app',
      recorded_by: null,
      created_at: at,
    })
  })
}

/**
 * Raise or re-raise a request. Mirrors the app's upsert plus
 * qualifications_insert_request / qualifications_update_own_request: a coach can
 * move a row to pending and never to approved.
 */
export function requestQualification(coachId: string, serviceId: string): Qualification {
  return mutate((db) => {
    const at = nowIso()
    const existing = db.qualifications.find(
      (q) => q.coach_id === coachId && q.service_id === serviceId,
    )
    if (existing) {
      if (existing.status === 'approved') return { ...existing }
      existing.status = 'pending'
      existing.requested_at = at
      existing.updated_at = at
      return { ...existing }
    }
    const row: Qualification = {
      id: newId(),
      coach_id: coachId,
      service_id: serviceId,
      status: 'pending',
      requested_at: at,
      approved_by: null,
      approved_at: null,
      revoked_by: null,
      revoked_at: null,
      revoke_reason: null,
      notes: null,
      created_at: at,
      updated_at: at,
    }
    db.qualifications.push(row)
    return { ...row }
  })
}

/**
 * The human gate. Completing the modules makes a coach eligible; this is what
 * makes them qualified, and from here the service's events start appearing in
 * their schedule.
 */
export const SELF_APPROVAL_MESSAGE =
  'You cannot approve your own qualification. Michel or another approver has to take this call.'

export function approveQualification(qualificationId: string, approverId: string): Qualification {
  return mutate((db) => {
    const row =
      db.qualifications.find((q) => q.id === qualificationId) ??
      fail('That qualification request is gone.')
    // A delegated approver is still a coach with their own requests. The SQL
    // policy has no coach_id <> auth.uid() guard, so the app layer is the line.
    if (row.coach_id === approverId) fail(SELF_APPROVAL_MESSAGE)
    const at = nowIso()
    row.status = 'approved'
    row.approved_by = approverId
    row.approved_at = at
    // A previously revoked row must be cleared, or is_coach_qualified() keeps
    // answering false and the approval silently does nothing.
    row.revoked_at = null
    row.revoked_by = null
    row.revoke_reason = null
    row.updated_at = at

    const service = db.services.find((s) => s.id === row.service_id)
    if (service) {
      insertNotification(
        db,
        buildQualificationApprovedNotification(row.coach_id, row.id, service, at),
        at,
      )
    }
    return { ...row }
  })
}

/**
 * Turn a qualification off. Used both for declining a request that is not ready
 * and for withdrawing one that was granted: the schema has a single 'revoked'
 * state and the reason is what tells them apart on screen.
 */
export function revokeQualification(
  qualificationId: string,
  actorId: string,
  reason: string,
): Qualification {
  return mutate((db) => {
    const row =
      db.qualifications.find((q) => q.id === qualificationId) ??
      fail('That qualification request is gone.')
    const at = nowIso()
    const wasApproved = row.status === 'approved'
    row.status = 'revoked'
    row.revoked_by = actorId
    row.revoked_at = at
    row.revoke_reason = reason
    row.updated_at = at

    // The reason is promised to the coach, so it goes to their inbox as well as
    // sitting on the row. Mirrors what api.ts writes on the Supabase path.
    const service = db.services.find((s) => s.id === row.service_id)
    if (service) {
      insertNotification(
        db,
        buildQualificationDecisionNotification(row.coach_id, row.id, service, reason, wasApproved, at),
        at,
      )
    }
    return { ...row }
  })
}

/* -------------------------------------------------------------------------- */
/* Notifications, sanctions, devices                                          */
/* -------------------------------------------------------------------------- */

/**
 * Who is signed in to the demo. AuthProvider keeps the demo session under this
 * key; the store reads it so a write can be refused the way RLS would refuse
 * it, without the screens having to pass an actor around.
 */
const DEMO_SESSION_KEY = 'lafolie-demo-session'

export function currentActorId(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage.getItem(DEMO_SESSION_KEY)
  } catch {
    return null
  }
}

/**
 * Mirror of public.acknowledge_notification() under RLS: a coach may only
 * update their own rows (notification_queue_update_own), an admin any row
 * (notification_queue_admin_all). The SQL coalesces, so a second tap keeps the
 * first timestamp instead of moving it.
 */
export function acknowledgeNotification(notificationId: string, actorId: string | null): void {
  mutate((db) => {
    const row = db.notificationQueue.find((n) => n.id === notificationId)
    if (!row) return
    const actor = actorId ? db.profiles.find((p) => p.id === actorId) : undefined
    const allowed = actor !== undefined && (actor.id === row.coach_id || actor.role === 'admin')
    if (!allowed) fail('That reminder is not yours to acknowledge.')
    if (!row.acknowledged_at) row.acknowledged_at = nowIso()
  })
}

export function acknowledgeSanction(sanctionId: string): void {
  mutate((db) => {
    const row = db.sanctions.find((s) => s.id === sanctionId)
    if (row && !row.acknowledged_at) {
      row.acknowledged_at = nowIso()
      row.updated_at = row.acknowledged_at
    }
  })
}

export function savePushSubscription(
  row: Pick<
    PushSubscriptionRow,
    'coach_id' | 'endpoint' | 'p256dh' | 'auth' | 'user_agent' | 'platform'
  >,
): void {
  mutate((db) => {
    const at = nowIso()
    const existing = db.pushSubscriptions.find((s) => s.endpoint === row.endpoint)
    if (existing) {
      Object.assign(existing, row, { is_active: true, failure_count: 0, updated_at: at })
      return
    }
    db.pushSubscriptions.push({
      id: newId(),
      ...row,
      is_active: true,
      failure_count: 0,
      last_success_at: null,
      last_failure_at: null,
      created_at: at,
      updated_at: at,
    })
  })
}

export function deactivatePushSubscription(endpoint: string): void {
  mutate((db) => {
    const existing = db.pushSubscriptions.find((s) => s.endpoint === endpoint)
    if (existing) {
      existing.is_active = false
      existing.updated_at = nowIso()
    }
  })
}
